import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import * as Sentry from '@sentry/angular';

/**
 * Global HTTP error interceptor.
 *
 * - Logs all HTTP errors to Sentry with appropriate severity levels
 * - Client errors (4xx) logged at 'warning' level
 * - Server errors (5xx) logged at 'error' level
 * - Adds request timing breadcrumbs for performance monitoring
 * - Lets errors propagate so services retain their own retry/catch logic
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const startTime = Date.now();

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const duration = Date.now() - startTime;

      // Add a breadcrumb for every failed request (useful for debugging chains)
      Sentry.addBreadcrumb({
        category: 'http',
        message: `${req.method} ${req.urlWithParams} — ${error.status} (${duration}ms)`,
        level: error.status >= 500 ? 'error' : 'warning',
        data: {
          method: req.method,
          url: req.urlWithParams,
          status: error.status,
          duration,
        },
      });

      // Report to Sentry with appropriate severity
      if (error.status >= 500) {
        // Server errors — always report
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            httpMethod: req.method,
            httpStatus: String(error.status),
          },
          extra: {
            url: req.urlWithParams,
            duration,
            statusText: error.statusText,
          },
        });
      } else if (error.status >= 400 && error.status !== 404) {
        // Client errors (except 404 which are expected during polling) — report as warning
        Sentry.captureMessage(`HTTP ${error.status}: ${req.method} ${req.urlWithParams}`, {
          level: 'warning',
          tags: {
            httpMethod: req.method,
            httpStatus: String(error.status),
          },
          extra: {
            url: req.urlWithParams,
            duration,
            statusText: error.statusText,
          },
        });
      }
      // 404s are silently tracked via breadcrumbs only (expected during polling)

      // Always re-throw — services keep their own retry/catch logic
      return throwError(() => error);
    })
  );
};
